import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { createRelatedDocument, getWorkOrderAttachments } from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { DocumentType, getDocumentTypeOptions } from "../workOrderEnums";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import { isSafeExternalUrl } from "../../cmms/cmmsUrl";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getMenuSx,
  getOutlinedButtonSx,
  getRadioSx,
  getSelectSx,
} from "../../cmms/assetHierarchyStyles";

const LINK_EXTERNAL = "external";
const LINK_INTERNAL = "internal";

const emptyForm = {
  documentType: DocumentType.Other,
  title: "",
  linkMode: LINK_EXTERNAL,
  externalUrl: "",
  attachmentId: null,
};

export default function RelatedDocumentFormDialog({ open, onClose, workOrderId, companyKey, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const attachmentsQuery = useQuery(
    ["cmmsWorkOrderAttachments", companyKey, workOrderId],
    () => getWorkOrderAttachments(companyKey, workOrderId),
    { enabled: open && Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const attachmentOptions = useMemo(
    () => (attachmentsQuery.data ?? []).map((a) => ({ id: a.id, title: a.fileName })),
    [attachmentsQuery.data],
  );

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setErrors({});
    }
  }, [open]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleTextChange = (event) => {
    handleField(event.target.name || event.target.id, event.target.value);
  };

  const validate = () => {
    const next = {};
    if (!form.title?.trim()) next.title = intl.formatMessage(messages.relatedDocumentsTitleRequired);
    if (form.linkMode === LINK_EXTERNAL) {
      if (!form.externalUrl?.trim()) {
        next.externalUrl = intl.formatMessage(messages.relatedDocumentsUrlRequiredError);
      } else if (!isSafeExternalUrl(form.externalUrl)) {
        // Same rule the server applies; catching it here saves a round trip and puts the
        // message on the field instead of in a toast.
        next.externalUrl = intl.formatMessage(messages.relatedDocumentsUrlInvalidError);
      }
    } else if (form.linkMode === LINK_INTERNAL) {
      if (!form.attachmentId) next.attachmentId = intl.formatMessage(messages.relatedDocumentsAttachmentRequiredError);
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        companyKey,
        documentType: form.documentType,
        title: form.title.trim(),
        externalUrl: form.linkMode === LINK_EXTERNAL ? form.externalUrl.trim() : null,
        attachmentId: form.linkMode === LINK_INTERNAL ? form.attachmentId : null,
      };
      await createRelatedDocument(workOrderId, payload);
      pushNotification(intl.formatMessage(messages.toastDocumentLinked));
      onSuccess?.();
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.relatedDocumentsAddFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  const attachmentValue = attachmentOptions.find((option) => option.id === form.attachmentId) ?? null;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.relatedDocumentsDialogTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.relatedDocumentsDocumentType} /></Typography>
          <Select
            fullWidth
            size="small"
            value={form.documentType}
            onChange={(event) => handleField("documentType", event.target.value)}
            sx={getSelectSx(cmmsColors)}
            MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
          >
            {getDocumentTypeOptions(intl).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </Box>

        <AxionControls.AxionTextBox
          name="title"
          label={intl.formatMessage(messages.fieldTitleRequired)}
          value={form.title}
          onChange={handleTextChange}
          error={errors.title}
        />

        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}><FormattedMessage {...messages.relatedDocumentsLinkType} /></Typography>
          <RadioGroup
            row
            value={form.linkMode}
            onChange={(event) => handleField("linkMode", event.target.value)}
          >
            <FormControlLabel value={LINK_EXTERNAL} control={<Radio size="small" sx={getRadioSx(cmmsColors)} />} label={intl.formatMessage(messages.relatedDocumentsExternalUrl)} />
            <FormControlLabel value={LINK_INTERNAL} control={<Radio size="small" sx={getRadioSx(cmmsColors)} />} label={intl.formatMessage(messages.relatedDocumentsInternalAttachment)} />
          </RadioGroup>
        </Box>

        {form.linkMode === LINK_EXTERNAL &&
          <AxionControls.AxionTextBox
            name="externalUrl"
            label={intl.formatMessage(messages.relatedDocumentsUrlRequired)}
            value={form.externalUrl}
            onChange={handleTextChange}
            error={errors.externalUrl}
          />
        }
        {form.linkMode === LINK_INTERNAL &&
          <AxionControls.AxionAutocomplete
            name="attachmentId"
            label={intl.formatMessage(messages.relatedDocumentsAttachmentRequired)}
            options={attachmentOptions}
            value={attachmentValue}
            onChange={(_event, option) => handleField("attachmentId", option?.id ?? null)}
            error={errors.attachmentId}
          />
        }

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.add} /></Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
